import React, { useEffect, useRef, useState } from 'react';
import { Select, Spin } from 'antd';

export interface OpcionRemota {
  value: number;
  label: string;
  /** Texto secundario (municipio, código, fase…). */
  detalle?: string;
}

interface SelectorRemotoProps {
  buscar: (texto: string) => Promise<OpcionRemota[]>;
  value?: number | number[];
  onChange?: (value: any, opciones?: OpcionRemota | OpcionRemota[]) => void;
  /** Opciones ya conocidas (p. ej. el valor actual en la edición, o los titulares de la farmacia). */
  opcionesIniciales?: OpcionRemota[];
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  /** Mínimo de caracteres para buscar (el backend exige 2). */
  minimo?: number;
  /** Busca también con el texto vacío (catálogos pequeños como las EPC). */
  cargarAlAbrir?: boolean;
  'aria-label'?: string;
}

/**
 * Select con búsqueda en servidor (farmacias, clientes, operaciones, EPC). Espera 300 ms tras la
 * última tecla y descarta respuestas viejas. El texto buscado viaja por POST (nunca en la URL).
 */
export const SelectorRemoto: React.FC<SelectorRemotoProps> = ({
  buscar,
  value,
  onChange,
  opcionesIniciales = [],
  placeholder,
  multiple,
  disabled,
  minimo = 2,
  cargarAlAbrir,
  ...rest
}) => {
  const [opciones, setOpciones] = useState<OpcionRemota[]>(opcionesIniciales);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);
  const [buscado, setBuscado] = useState(false);
  const temporizador = useRef<ReturnType<typeof setTimeout>>();
  const ultima = useRef(0);

  // Mantiene visibles las opciones iniciales (valores ya elegidos) aunque cambien desde fuera.
  useEffect(() => {
    setOpciones((actuales) => {
      const ids = new Set(actuales.map((o) => o.value));
      const nuevas = opcionesIniciales.filter((o) => !ids.has(o.value));
      return nuevas.length ? [...nuevas, ...actuales] : actuales;
    });
  }, [opcionesIniciales]);

  const lanzar = (texto: string) => {
    if (temporizador.current) clearTimeout(temporizador.current);
    if (texto.trim().length < minimo && !(cargarAlAbrir && texto.trim() === '')) {
      setBuscado(false);
      return;
    }
    temporizador.current = setTimeout(async () => {
      const n = ++ultima.current;
      setCargando(true);
      setFallo(false);
      try {
        const encontradas = await buscar(texto.trim());
        if (n !== ultima.current) return;
        setBuscado(true);
        // Conserva las elegidas para que el Select no pierda su etiqueta.
        const elegidas = new Set(Array.isArray(value) ? value : value !== undefined ? [value] : []);
        setOpciones((actuales) => {
          const conservar = actuales.filter((o) => elegidas.has(o.value));
          const ids = new Set(conservar.map((o) => o.value));
          return [...conservar, ...encontradas.filter((o) => !ids.has(o.value))];
        });
      } catch {
        // Un error o un tiempo de espera agotado no es «sin resultados» (e2e IT09).
        if (n === ultima.current) setFallo(true);
      } finally {
        if (n === ultima.current) setCargando(false);
      }
    }, 300);
  };

  return (
    <Select
      {...rest}
      mode={multiple ? 'multiple' : undefined}
      showSearch
      allowClear
      value={value as any}
      disabled={disabled}
      placeholder={placeholder}
      filterOption={false}
      onSearch={lanzar}
      onDropdownVisibleChange={(abierto) => abierto && cargarAlAbrir && lanzar('')}
      onChange={(v, o) => onChange?.(v, o as any)}
      notFoundContent={cargando
        ? <Spin size="small" />
        : fallo ? 'No se ha podido buscar. Vuelve a escribir para reintentar.'
          : buscado ? 'Sin resultados' : `Escribe al menos ${minimo} caracteres`}
      options={opciones.map((o) => ({
        value: o.value,
        label: o.detalle ? `${o.label} · ${o.detalle}` : o.label,
      }))}
      style={{ width: '100%' }}
    />
  );
};
